import React, { useEffect, useState } from "react";
import { Form, Radio, Space, Divider, Switch, Input, Button, Tooltip } from "antd";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { selectRole } from "modules/auth/store/authSlice";
import { allowPermission } from "helper/util";
import { canCreatePermission } from "configs/constants";
import { canEditPermission } from "configs/constants";

const NOTIFICATION_BY = {
  SMS: "SMS",
  EMAIL: "EMAIL",
  SMS_AND_EMAIL: "SMS_AND_EMAIL"
};

const NotificationSettingsForm = ({ isFetching, setting, handleSubmit }) => {
  const [form] = Form.useForm();

  const [copied, setCopied] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [enableNotifyStartJob, setEnableNotifyStartJob] = useState(false);
  const [enableNotifyArrival, setEnableNotifyArrival] = useState(false);
  const [enableNotifyStopCompletion, setEnableNotifyStopCompletion] = useState(false);
  const [enableNotifyGoingNext, setEnableNotifyGoingNext] = useState(false);
  const [enableNotifyStopFailed, setEnableNotifyStopFailed] = useState(true);
  const [enableNotifyIncompletePickup, setEnableNotifyIncompletePickup] = useState(false);
  const [sendScannedDocumentsOnDropoff, setSendScannedDocumentsOnDropoff] =
    useState(false);
  const [sendPodOnDropoffComplete, setSendPodOnDropoffComplete] = useState(false);
  const [clientFailNotify, setClientFailNotify] = useState(false);
  const [startJobNotificationContent, setStartJobNotificationContent] = useState("");
  const [arrivalNotificationContent, setArrivalNotificationContent] = useState("");
  const [stopCompletionNotificationContent, setStopCompletionNotificationContent] =
    useState("");
  const [stopFailedNotificationContent, setStopFailedNotificationContent] = useState("");
  const [goingNextNotificationContent, setGoingNextNotificationContent] = useState("");
  const [incompletePickupNotificationContent, setIncompletePickupNotificationContent] =
    useState("");
  const role = useSelector(selectRole);
  const canCreate = allowPermission(role, canCreatePermission);
  const canEdit = allowPermission(role, canEditPermission);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCopied(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (setting) {
      form.setFieldsValue({
        ...setting,
        notificationBy:
          setting.enableSendEmail && setting.enableSendSMS
            ? NOTIFICATION_BY.SMS_AND_EMAIL
            : setting.enableSendEmail
            ? NOTIFICATION_BY.EMAIL
            : setting?.enableSendSMS
            ? NOTIFICATION_BY.SMS
            : ""
      });
      setEnableNotifyStartJob(setting.enableNotifyStartJob);
      setEnableNotifyArrival(setting.enableNotifyArrival);
      setEnableNotifyStopCompletion(setting.enableNotifyStopCompletion);
      setEnableNotifyGoingNext(setting.enableNotifyGoingNext);
      setEnableNotifyStopFailed(setting.enableNotifyStopFailed);
      setEnableNotifyIncompletePickup(setting.enableNotifyIncompletePickup);
      setSendScannedDocumentsOnDropoff(setting.sendScannedDocumentsOnDropoff || false);
      setSendPodOnDropoffComplete(setting.sendPodOnDropoffComplete || false);
      setClientFailNotify(setting.clientFailNotify ?? false);
      setStartJobNotificationContent(setting.startJobNotificationContent);
      setArrivalNotificationContent(setting.arrivalNotificationContent);
      setStopCompletionNotificationContent(setting.stopCompletionNotificationContent);
      setStopFailedNotificationContent(setting.stopFailedNotificationContent);
      setGoingNextNotificationContent(setting.goingNextNotificationContent);
      setIncompletePickupNotificationContent(setting.incompletePickupNotificationContent);

      // Set initial values for change detection
      setInitialValues({
        ...setting,
        notificationBy:
          setting.enableSendEmail && setting.enableSendSMS
            ? NOTIFICATION_BY.SMS_AND_EMAIL
            : setting.enableSendEmail
            ? NOTIFICATION_BY.EMAIL
            : setting?.enableSendSMS
            ? NOTIFICATION_BY.SMS
            : ""
      });
      setHasChanges(false);
    }
    // eslint-disable-next-line
  }, [setting]);

  const handleFormValuesChange = () => {
    const currentValues = form.getFieldsValue();

    // Check if any field has changed (this is a simplified check)
    const hasFormChanges =
      Object.keys(currentValues).some(
        (key) => currentValues[key] !== initialValues[key]
      ) ||
      enableNotifyStartJob !== initialValues?.enableNotifyStartJob ||
      enableNotifyArrival !== initialValues?.enableNotifyArrival ||
      enableNotifyStopCompletion !== initialValues?.enableNotifyStopCompletion ||
      enableNotifyGoingNext !== initialValues?.enableNotifyGoingNext ||
      enableNotifyStopFailed !== initialValues?.enableNotifyStopFailed ||
      enableNotifyIncompletePickup !== initialValues?.enableNotifyIncompletePickup ||
      sendScannedDocumentsOnDropoff !== initialValues?.sendScannedDocumentsOnDropoff ||
      sendPodOnDropoffComplete !== initialValues?.sendPodOnDropoffComplete ||
      clientFailNotify !== initialValues?.clientFailNotify;

    setHasChanges(hasFormChanges);
  };

  const handleOnFinish = async (values) => {
    const params = { ...values };
    params.id = setting?.id;
    params.enableNotifyStartJob = enableNotifyStartJob;
    params.enableNotifyArrival = enableNotifyArrival;
    params.enableNotifyStopCompletion = enableNotifyStopCompletion;
    params.enableNotifyGoingNext = enableNotifyGoingNext;
    params.enableNotifyStopFailed = enableNotifyStopFailed;
    params.enableNotifyIncompletePickup = enableNotifyIncompletePickup;
    params.sendScannedDocumentsOnDropoff = sendScannedDocumentsOnDropoff;
    params.sendPodOnDropoffComplete = sendPodOnDropoffComplete;
    params.clientFailNotify = clientFailNotify;
    params.enableSendEmail =
      params.notificationBy === NOTIFICATION_BY.EMAIL ||
      params.notificationBy === NOTIFICATION_BY.SMS_AND_EMAIL;
    params.enableSendSMS =
      params.notificationBy === NOTIFICATION_BY.SMS ||
      params.notificationBy === NOTIFICATION_BY.SMS_AND_EMAIL;
    handleSubmit(params);
  };

  const previewMessage = (message) => {
    let preview = message;
    setting?.notificationFields &&
      Object.keys(setting?.notificationFields).forEach((field) => {
        preview = preview?.replaceAll(`{${field}}`, setting.notificationFields[field]);
      });
    return preview;
  };

  const showActionButton = canCreate || (canEdit && setting?.id);
  return (
    <div className="common-form notification-setting-form">
      <div className="sticky-form-header">
        <h3>Notification Center</h3>
        <div className="sticky-form-actions">
          <Button
            loading={isFetching}
            type="primary"
            disabled={!hasChanges}
            onClick={() => form.submit()}
          >
            {setting?.id ? "Update" : "Create"}
            <CheckCircleOutlined />
          </Button>
        </div>
      </div>
      <Form
        form={form}
        scrollToFirstError
        layout="vertical"
        hideRequiredMark
        colon={false}
        onFinish={handleOnFinish}
        onValuesChange={handleFormValuesChange}
      >
        <div className="form-content">
          <Form.Item
            label="How will your customers receive notifications?"
            name="notificationBy"
            rules={[{ required: true, message: "Required" }]}
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value={NOTIFICATION_BY.SMS}>Text Message</Radio>
                <Radio value={NOTIFICATION_BY.EMAIL}>Email</Radio>
                <Radio value={NOTIFICATION_BY.SMS_AND_EMAIL}>Text Message & Email</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Divider />
          <label>What does the notification message say?</label>
          <div className="custom-fields">
            <div className="title">
              These notification fields are available <i>(Click to copy)</i>{" "}
              {copied ? <span className="copied"> Copied </span> : null}
            </div>
            <div className="fields">
              {setting?.notificationFields &&
                Object.keys(setting?.notificationFields).map((field) => (
                  <CopyToClipboard
                    key={field}
                    text={`{${field}}`}
                    onCopy={() => setCopied(true)}
                  >
                    <div className="filed">{field}</div>
                  </CopyToClipboard>
                ))}
            </div>
          </div>
          <div className="notification-messages">
            <h1>Client Notifications</h1>
            <div className="client-noti-messages">
              <div className="item">
                <Form.Item>
                  <Switch
                    checked={clientFailNotify}
                    onChange={(checked) => {
                      setClientFailNotify(checked);
                      handleFormValuesChange();
                      setHasChanges(true);
                    }}
                  />{" "}
                  Receive Failed Stop Notifications
                </Form.Item>
                <div className="desc">
                  If this is on, you will receive real-time fail notifications to your
                  Support Email address. If disabled, you will not receive failure
                  notifications.
                </div>
              </div>
              <Divider type="vertical" />
              <div className="item">
                <Form.Item>
                  <Switch
                    checked={enableNotifyIncompletePickup}
                    onChange={(checked) => {
                      setEnableNotifyIncompletePickup(checked);
                      handleFormValuesChange();
                    }}
                  />{" "}
                  Incomplete Pickup / Dropoff
                </Form.Item>
                <div className="pickup-note">
                  <strong>Note:</strong> Due to multiple packages batched in the pickup,
                  it may not be possible to identify which package wasn&apos;t picked up.
                  As such we do NOT send notification to recipient, but to your Support
                  Email address on file as well as to our dispatch support team. It will
                  include all of the details for you to follow up with the driver or your
                  customers.
                </div>
              </div>
            </div>
            <h2 style={{ marginTop: "24px", marginBottom: "16px", fontSize: "16px" }}>
              Document Notifications
            </h2>
            <div className="document-noti-messages">
              <div className="item">
                <Form.Item>
                  <Switch
                    checked={sendScannedDocumentsOnDropoff}
                    onChange={(checked) => {
                      setSendScannedDocumentsOnDropoff(checked);
                      handleFormValuesChange();
                    }}
                  />{" "}
                  Receive Email for Scanned Documents on Dropoff
                </Form.Item>
                <div className="desc">
                  When a driver completes a dropoff using the Document Scanner, these are
                  sent to the client support email.
                </div>
              </div>
              <Divider type="vertical" />
              <div className="item">
                <Form.Item>
                  <Switch
                    checked={sendPodOnDropoffComplete}
                    onChange={(checked) => {
                      setSendPodOnDropoffComplete(checked);
                      handleFormValuesChange();
                    }}
                  />{" "}
                  Receive Email for POD on Dropoff
                </Form.Item>
                <div className="desc">
                  When a driver completes a dropoff, the Proof of Delivery (POD) is sent
                  to the client support email.
                </div>
              </div>
            </div>
            <Divider />
            <h1>Customer Pickup Notifications</h1>
            <div className="pickup-noti-messages">
              <div className="item">
                <Tooltip title="Coming Soon">
                  <Form.Item style={{ opacity: 0.5, cursor: "not-allowed" }}>
                    <Switch disabled /> Pickup Going Next
                  </Form.Item>
                </Tooltip>
                <div className="desc" style={{ opacity: 0.5 }}>
                  When the driver is heading to the pickup location.
                </div>
              </div>
              <Divider type="vertical" />
              <div className="item">
                <Form.Item>
                  <Switch
                    checked={enableNotifyStartJob}
                    onChange={(checked) => {
                      setEnableNotifyStartJob(checked);
                      handleFormValuesChange();
                    }}
                  />{" "}
                  Pickup Arrival
                </Form.Item>
                <div className="desc">
                  When the driver hits Arrive at Pickup location, a message is sent to all
                  the Delivery stops main contact.
                </div>
                <Form.Item
                  name="startJobNotificationContent"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input.TextArea
                    rows={4}
                    onChange={(e) => setStartJobNotificationContent(e.target.value)}
                  />
                </Form.Item>
                <div className="desc preview">Preview on mobile:</div>
                <div className="preview-content">
                  {previewMessage(startJobNotificationContent)}
                </div>
              </div>
              <Divider type="vertical" />
              <div className="item">
                <Tooltip title="Coming Soon">
                  <Form.Item style={{ opacity: 0.5, cursor: "not-allowed" }}>
                    <Switch disabled /> Pickup Completion
                  </Form.Item>
                </Tooltip>
                <div className="desc" style={{ opacity: 0.5 }}>
                  When the driver completes the pickup.
                </div>
              </div>
              <Divider type="vertical" />
              <div className="item">
                <Tooltip title="Coming Soon">
                  <Form.Item style={{ opacity: 0.5, cursor: "not-allowed" }}>
                    <Switch disabled /> Failed Pickup
                  </Form.Item>
                </Tooltip>
                <div className="desc" style={{ opacity: 0.5 }}>
                  When the driver is unable to complete the pickup.
                </div>
              </div>
            </div>
            <Divider />
            <h1>Customer Drop-off Notifications</h1>
            <div className="dropoff-noti-messages">
              <div className="item">
                <Form.Item>
                  <Switch
                    checked={enableNotifyGoingNext}
                    onChange={(checked) => {
                      setEnableNotifyGoingNext(checked);
                      handleFormValuesChange();
                    }}
                  />{" "}
                  Drop-off Going Next
                </Form.Item>
                <div className="desc">
                  When the driver hits “Going Next” to indicate that he is going next drop
                  off stop to delivery customer’s order.`
                </div>
                <Form.Item
                  name="goingNextNotificationContent"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input.TextArea
                    rows={4}
                    onChange={(e) => setGoingNextNotificationContent(e.target.value)}
                  />
                </Form.Item>
                <div className="desc preview">Preview on mobile:</div>
                <div className="preview-content">
                  {previewMessage(goingNextNotificationContent)}
                </div>
              </div>
              <Divider type="vertical" />
              <div className="item">
                <Form.Item>
                  <Switch
                    checked={enableNotifyArrival}
                    onChange={(checked) => {
                      setEnableNotifyArrival(checked);
                      handleFormValuesChange();
                    }}
                  />{" "}
                  Drop-off Arrival
                </Form.Item>
                <div className="desc">
                  When the driver hits “Arrive” to indicate that he is arrived to your
                  customer’s location.
                </div>
                <Form.Item
                  name="arrivalNotificationContent"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input.TextArea
                    rows={4}
                    onChange={(e) => setArrivalNotificationContent(e.target.value)}
                  />
                </Form.Item>
                <div className="desc preview">Preview on mobile:</div>
                <div className="preview-content">
                  {previewMessage(arrivalNotificationContent)}
                </div>
              </div>
              <Divider type="vertical" />
              <div className="item">
                <Form.Item>
                  <Switch
                    checked={enableNotifyStopCompletion}
                    onChange={(checked) => {
                      setEnableNotifyStopCompletion(checked);
                      handleFormValuesChange();
                    }}
                  />{" "}
                  Drop-off Completion
                </Form.Item>
                <div className="desc">
                  When the driver hits &quot;Complete&quot; to indicate that he has
                  completed the customer&apos;s order.`
                </div>
                <Form.Item
                  name="stopCompletionNotificationContent"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input.TextArea
                    rows={4}
                    onChange={(e) => setStopCompletionNotificationContent(e.target.value)}
                  />
                </Form.Item>
                <div className="desc preview">Preview on mobile:</div>
                <div className="preview-content">
                  {previewMessage(stopCompletionNotificationContent)}
                </div>
              </div>
              <Divider type="vertical" />
              <div className="item">
                <Form.Item>
                  <Switch
                    checked={enableNotifyStopFailed}
                    onChange={(checked) => {
                      setEnableNotifyStopFailed(checked);
                      handleFormValuesChange();
                    }}
                  />{" "}
                  Failed Delivery
                </Form.Item>
                <div className="desc">
                  When the driver isn&apos;t able to deliver the package(s)
                </div>
                <Form.Item
                  name="stopFailedNotificationContent"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input.TextArea
                    rows={4}
                    onChange={(e) => setStopFailedNotificationContent(e.target.value)}
                  />
                </Form.Item>
                <div className="desc preview">Preview on mobile:</div>
                <div className="preview-content">
                  {previewMessage(stopFailedNotificationContent)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default NotificationSettingsForm;
